import { Injectable, signal, computed, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { User, AuthTokens } from '../models';
import { STORAGE_KEYS } from '../constants';
import { 
  getLocalStorage, 
  setLocalStorage, 
  removeLocalStorage,
  getSessionStorage,
  setSessionStorage,
  removeSessionStorage 
} from '../utils/storage.utils';
import { LoggingService } from './logging.service';
import { PermissionsService } from './permissions.service';

/**
 * Authentication state service
 * Manages user authentication state, tokens, and user data
 * Uses Angular signals for reactive state management
 * 
 * Usage:
 * ```
 * constructor(private authState: AuthStateService) {}
 * 
 * // Check if authenticated
 * if (this.authState.isAuthenticated()) {
 *   // User is logged in
 * }
 * 
 * // Get current user
 * const user = this.authState.currentUser();
 * 
 * // Sign in
 * this.authState.signIn(user, tokens);
 * 
 * // Sign out
 * this.authState.signOut();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  // Signals for reactive state
  private userSignal = signal<User | null>(null);
  private tokenSignal = signal<string | null>(null); // Store JWT token as string
  private loadingSignal = signal<boolean>(false);

  // Computed signals
  readonly currentUser = this.userSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isLoading = this.loadingSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.userSignal() !== null && this.tokenSignal() !== null);

  constructor(
    private router: Router,
    private logger: LoggingService,
    private injector: Injector
  ) {
    this.initializeAuthState();
  }

  /**
   * Initialize auth state from storage
   * Checks both sessionStorage and localStorage (in that order)
   * Matches old system: token in both storages, user in sessionStorage/localStorage
   */
  private initializeAuthState(): void {
    try {
      // First check sessionStorage (current session)
      let token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      let user = getSessionStorage<User>(STORAGE_KEYS.USER);

      this.logger.info('Initializing auth state', { 
        hasSessionToken: !!token, 
        hasSessionUser: !!user 
      });

      // If not in session storage, check localStorage (remember me)
      if (!token) {
        token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        this.logger.info('Token not in session, checking localStorage', { hasLocalToken: !!token });
      }
      if (!user) {
        user = getLocalStorage<User>(STORAGE_KEYS.USER);
        this.logger.info('User not in session, checking localStorage', { hasLocalUser: !!user });
      }

      if (user && token) {
        // Restore auth state (tokens don't expire)
        this.userSignal.set(user);
        this.tokenSignal.set(token);
        this.logger.info('Auth state restored from storage', { userId: user._id });
      } else {
        this.logger.info('No complete auth state found in storage', { hasUser: !!user, hasToken: !!token });
      }
    } catch (error) {
      this.logger.error('Failed to initialize auth state', error);
      this.clearAuthState();
    }
  }

  /**
   * Sign in user
   * @param user User data
   * @param tokens Authentication tokens
   * @param rememberMe If true, store in localStorage for persistence
   */
  signIn(user: User, tokens: AuthTokens, rememberMe: boolean = false): void {
    try {
      const token = tokens.accessToken;

      // Store in state
      this.userSignal.set(user);
      this.tokenSignal.set(token);

      // Always persist to sessionStorage (for current session)
      // Matches old system: token and currentUser keys
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
      setSessionStorage(STORAGE_KEYS.USER, user);

      // If remember me is enabled, also persist to localStorage
      if (rememberMe) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        setLocalStorage(STORAGE_KEYS.USER, user);
      }

      this.logger.info('User signed in', { userId: user._id, rememberMe });
    } catch (error) {
      this.logger.error('Failed to sign in', error);
      throw error;
    }
  }

  /**
   * Sign out user
   */
  signOut(navigate: boolean = true): void {
    try {
      const userId = this.userSignal()?._id;

      // Clear state
      this.clearAuthState();

      // Clear permissions
      try {
        const permissionsService = this.injector.get(PermissionsService, null);
        if (permissionsService) {
          permissionsService.clearPermissions();
        }
      } catch {
        // Ignore if service not available
      }

      this.logger.info('User signed out', { userId });

      // Navigate to login
      if (navigate) {
        this.router.navigate(['/auth/login']);
      }
    } catch (error) {
      this.logger.error('Failed to sign out', error);
    }
  }

  /**
   * Update user data
   */
  updateUser(user: Partial<User>): void {
    const currentUser = this.userSignal();
    if (!currentUser) {
      return;
    }

    const updatedUser = { ...currentUser, ...user };
    this.userSignal.set(updatedUser);
    
    // Update in both storages
    setSessionStorage(STORAGE_KEYS.USER, updatedUser);
    const localStorageUser = getLocalStorage<User>(STORAGE_KEYS.USER);
    if (localStorageUser) {
      setLocalStorage(STORAGE_KEYS.USER, updatedUser);
    }

    this.logger.info('User data updated');
  }

  /**
   * Update tokens
   */
  updateTokens(tokens: AuthTokens): void {
    const token = tokens.accessToken;
    this.tokenSignal.set(token);
    
    // Update in both storages
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
    const localStorageToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (localStorageToken) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    }

    this.logger.info('Tokens updated');
  }

  /**
   * Get access token (JWT)
   */
  getAccessToken(): string | null {
    // First check signal, then fallback to storage
    let token = this.tokenSignal();
    if (!token) {
      token = sessionStorage.getItem(STORAGE_KEYS.TOKEN) || localStorage.getItem(STORAGE_KEYS.TOKEN);
    }
    return token;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    // Not used in JWT flow, kept for compatibility
    return null;
  }

  /**
   * Clear all auth state
   */
  private clearAuthState(): void {
    this.userSignal.set(null);
    this.tokenSignal.set(null);
    
    // Clear from both storages (both old and new keys)
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    removeSessionStorage(STORAGE_KEYS.USER);
    removeSessionStorage(STORAGE_KEYS.AUTH_TOKEN); // Old key
    
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    removeLocalStorage(STORAGE_KEYS.USER);
    removeLocalStorage(STORAGE_KEYS.AUTH_TOKEN); // Old key
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this.loadingSignal.set(loading);
  }
}

